// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack API Hooks — Investor Dashboard
// apps/web/src/lib/api/investor.ts
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import type { ApiError } from './types';

// Types match the C# DTOs
export interface InvestorDashboardData {
  projectName: string;
  customTitle: string | null;
  companyName: string;
  logoUrl: string | null;
  compressionRatio: number;
  totalEquivalentWeeks: number;
  totalActualHours: number;
  replacementCostEstimate: number | null;
  impliedTeamSize: number;
  costPerEquivalentWeek: number;
  projectStartDate: string;
  calendarDaysElapsed: number;
  phases: PhaseTimelineEntry[];
  velocityTimeline: VelocityDataPoint[];
  complexity: ComplexityBreakdown;
  totalWorkItems: number;
  completedWorkItems: number;
  totalSessions: number;
  totalDecisions: number | null;
  totalContributors: number | null;
  totalAITokens: number | null;
  totalAICost: number | null;
  benchmarkComparison: BenchmarkComparison | null;
  generatedAt: string;
  poweredBy: string;
}

export interface PhaseTimelineEntry {
  name: string;
  orderIndex: number;
  status: string;
  plannedEffortWeeks: number;
  equivalentEffortWeeks: number;
  actualCalendarDays: number;
  expertiseDomains: string[];
  workItemCount: number;
  completedWorkItems: number;
}

export interface VelocityDataPoint {
  period: string;
  actualHours: number;
  equivalentWeeks: number;
  compressionRatio: number;
  cumulativeEquivalentWeeks: number;
  cumulativeReplacementCost: number;
  sessionsCount: number;
}

export interface ComplexityBreakdown {
  avgArchitecturalDepth: number;
  avgIntegrationPoints: number;
  avgDomainExpertise: number;
  avgInfrastructureExpertise: number;
  avgSecurityExpertise: number;
  avgNovelty: number;
  overallComplexityScore: number;
  totalExpertiseDomains: number;
}

export interface BenchmarkComparison {
  industryAvgWeeksForSimilar: number;
  industryAvgTeamSize: number;
  industryAvgCost: number;
  forgeTrackActualWeeks: number;
  costSavingsPercent: number;
  timeSavingsPercent: number;
}

export interface ShareLink {
  id: number;
  projectId: number;
  projectName: string;
  shareToken: string;
  shareUrl: string;
  title: string | null;
  createdDate: string;
  expiresDate: string | null;
  accessCount: number;
  isRevoked: boolean;
  showReplacementCost: boolean;
  showDecisions: boolean;
  showContributors: boolean;
  showAICosts: boolean;
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useInvestorDashboard(projectId: number) {
  const { createApi } = useApiClient();
  const [data, setData] = useState<InvestorDashboardData | null>(null);
  const [loading, setLoading] = useState(projectId > 0);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    if (projectId <= 0) return;
    try { setLoading(true); setError(null); const api = await createApi(); const res = await api.get<InvestorDashboardData>(`/api/v1/investor/dashboard?projectId=${projectId}`); setData(res.data); }
    catch (err: any) { setError({ message: err.response?.data?.message || err.message, status: err.response?.status || 500 }); }
    finally { setLoading(false); }
  }, [createApi, projectId]);

  useEffect(() => { if (projectId > 0) fetch(); }, [fetch, projectId]);
  return { data, loading, error, refetch: fetch };
}

/** Public dashboard — no auth needed, fetches via share token */
export function usePublicInvestorDashboard(shareToken: string) {
  const [data, setData] = useState<InvestorDashboardData | null>(null);
  const [loading, setLoading] = useState(!!shareToken);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!shareToken) return;
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(true);
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const res = await window.fetch(`${apiUrl}/api/v1/public/investor/${shareToken}`, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status === 404 ? 'Dashboard not found or link expired.' : 'Failed to load.');
        setData(await res.json());
      } catch (err: any) {
        if (err.name !== 'AbortError') setError({ message: err.message, status: 0 });
      } finally { setLoading(false); }
    })();
    return () => controller.abort();
  }, [shareToken]);

  return { data, loading, error };
}

export function useShareLinks(projectId?: number) {
  const { createApi } = useApiClient();
  const [data, setData] = useState<ShareLink[] | null>(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try { setLoading(true); const api = await createApi(); const qs = projectId ? `?projectId=${projectId}` : ''; const res = await api.get<ShareLink[]>(`/api/v1/investor/share-links${qs}`); setData(res.data); }
    catch {} finally { setLoading(false); }
  }, [createApi, projectId]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
}

export function useShareLinkMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const create = useCallback(async (data: { projectId: number; title?: string; expiresDate?: string; showReplacementCost?: boolean; showDecisions?: boolean; showContributors?: boolean; showAICosts?: boolean }): Promise<ShareLink | null> => {
    try { setLoading(true); const api = await createApi(); const res = await api.post<ShareLink>('/api/v1/investor/share-links', data); return res.data; }
    catch { return null; } finally { setLoading(false); }
  }, [createApi]);

  const revoke = useCallback(async (id: number): Promise<boolean> => {
    try { const api = await createApi(); await api.delete(`/api/v1/investor/share-links/${id}`); return true; }
    catch { return false; }
  }, [createApi]);

  return { create, revoke, loading };
}
